// Monthly Bank Summary — shown as a tab on the Bank Statement page.
//
// For each bank account and month you enter the statement's total money in
// and money out, then break the money out into categories (business, owner
// reimbursable, owner draw / personal, transfer). The Profit Sharing page uses
// these to work out business money out.
//
// Firestore:
//   bankMonthlySummaries/{accountId}_{YYYY-MM}
//     { accountId, accountLabel, accountType, month, moneyIn, moneyOut,
//       lines: [{ id, category, kind, amount, note }], notes, updatedAt }
//   appSettings/bankSpendCategories  { custom: [{ name, kind }] }
// Reads: bankStatementAccounts, bankStatementEntries (for "Fill from uploaded statement").

import React, { useEffect, useMemo, useState } from "react";
import { collection, getDocs, doc, setDoc, deleteDoc, query, where, serverTimestamp } from "firebase/firestore";
import { db } from "../firebaseConfig";
import "./FinancePages.css";
import BankPopup from "./bankPopup";
import { formatMoney, parseAmount } from "../utils/financeEntries";
import {
  KINDS,
  KIND_ORDER,
  mergeCategories,
  loadCustomCategories,
  saveCustomCategories,
  lineKind,
} from "../utils/bankSpendCategories";
import { summarizeMonth, summariesFromEntries } from "../utils/profitCalc";

const thisMonth = () => new Date().toISOString().slice(0, 7);
const lastMonth = () => {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - 1);
  return d.toISOString().slice(0, 7);
};
const monthLabel = (m) => {
  if (!m) return "";
  const [y, mo] = m.split("-").map(Number);
  return new Date(Date.UTC(y, mo - 1, 1)).toLocaleDateString("en-US", { month: "long", year: "numeric", timeZone: "UTC" });
};
const accountLabel = (a) =>
  [a.nickname || a.bankName, a.accountLast4 ? `••${a.accountLast4}` : "", a.accountType === "creditCard" ? "(Credit Card)" : a.accountType === "personal" ? "(Personal)" : ""]
    .filter(Boolean)
    .join(" ");
const newLine = () => ({ id: `l${Date.now()}${Math.random().toString(36).slice(2, 6)}`, category: "", kind: "", amount: "", note: "" });

export default function MonthlyBankSummary() {
  const [month, setMonth] = useState(lastMonth());
  const [accounts, setAccounts] = useState([]);
  const [summaries, setSummaries] = useState([]); // typed summaries
  const [bankEntries, setBankEntries] = useState([]); // uploaded / entered statement lines
  const [customCats, setCustomCats] = useState([]);
  const [loading, setLoading] = useState(true);

  const [editing, setEditing] = useState(null); // draft summary
  const [saving, setSaving] = useState(false);
  const [statementTotals, setStatementTotals] = useState(null);

  const [showNewCat, setShowNewCat] = useState(null); // line id that asked for it
  const [newCat, setNewCat] = useState({ name: "", kind: "business" });

  const categories = useMemo(() => mergeCategories(customCats), [customCats]);

  useEffect(() => {
    const load = async () => {
      try {
        const [accSnap, sumSnap, entrySnap, custom] = await Promise.all([
          getDocs(collection(db, "bankStatementAccounts")),
          getDocs(collection(db, "bankMonthlySummaries")),
          getDocs(collection(db, "bankStatementEntries")),
          loadCustomCategories(),
        ]);
        setBankEntries(entrySnap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setAccounts(accSnap.docs.map((d) => ({ id: d.id, ...d.data() })).sort((a, b) => accountLabel(a).localeCompare(accountLabel(b))));
        setSummaries(sumSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setCustomCats(custom);
      } catch (e) {
        console.error("Error loading monthly summaries", e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // Months with an uploaded statement come from those lines; typing is only for months with no upload.
  const uploaded = useMemo(() => summariesFromEntries(bankEntries, accounts), [bankEntries, accounts]);

  const rowsForMonth = accounts.map((a) => {
    const up = uploaded.find((x) => x.accountId === a.id && x.month === month);
    const typed = summaries.find((x) => x.accountId === a.id && x.month === month);
    const s = up || typed;
    return { account: a, summary: s, fromUpload: !!up, typedIgnored: !!(up && typed), calc: s ? summarizeMonth(s, categories) : null };
  });
  const monthTotals = rowsForMonth.reduce(
    (t, r) => {
      if (!r.calc || r.account.accountType === "personal") return t;
      t.moneyIn += r.calc.moneyIn;
      t.moneyOut += r.calc.moneyOut;
      t.business += r.calc.businessOut;
      t.owner += r.calc.byKind.owner_draw;
      t.transfer += r.calc.byKind.transfer;
      return t;
    },
    { moneyIn: 0, moneyOut: 0, business: 0, owner: 0, transfer: 0 }
  );
  const missingCount = rowsForMonth.filter((r) => !r.summary && r.account.accountType !== "personal").length;

  /* ------------------------------ editor ------------------------------ */

  const openUploaded = (account, summary) => {
    setStatementTotals(null);
    setEditing({ ...summary, accountLabel: accountLabel(account), readOnly: true });
  };

  const openEditor = async (account) => {
    const existing = summaries.find((x) => x.accountId === account.id && x.month === month);
    setEditing(
      existing
        ? { ...existing, accountLabel: accountLabel(account), accountType: account.accountType || existing.accountType || "operating", lines: (existing.lines || []).map((l) => ({ ...l, id: l.id || newLine().id })) }
        : {
            id: `${account.id}_${month}`,
            accountId: account.id,
            accountLabel: accountLabel(account),
            accountType: account.accountType || "operating",
            month,
            moneyIn: "",
            moneyOut: "",
            lines: [newLine()],
            notes: "",
          }
    );
    // Totals from any PDF/manual entries already on the Bank Statement page.
    setStatementTotals(null);
    try {
      const snap = await getDocs(query(collection(db, "bankStatementEntries"), where("bankAccountId", "==", account.id)));
      const rows = snap.docs.map((d) => d.data()).filter((e) => String(e.date || "").slice(0, 7) === month);
      if (rows.length) {
        const sum = (t) => rows.filter((e) => e.type === t).reduce((s, e) => s + parseAmount(e.amount), 0);
        setStatementTotals({ moneyIn: sum("moneyIn"), moneyOut: sum("moneyOut"), count: rows.length });
      }
    } catch (e) {
      console.warn("Could not read statement entries", e);
    }
  };

  const updateLine = (id, patch) =>
    setEditing((p) => ({ ...p, lines: p.lines.map((l) => (l.id === id ? { ...l, ...patch } : l)) }));
  const removeLine = (id) => setEditing((p) => ({ ...p, lines: p.lines.filter((l) => l.id !== id) }));

  const pickCategory = (id, value) => {
    if (value === "__new__") {
      setNewCat({ name: "", kind: "business" });
      setShowNewCat(id);
      return;
    }
    const cat = categories.find((c) => c.name === value);
    updateLine(id, { category: value, kind: cat ? cat.kind : "" });
  };

  const addCategory = async () => {
    const name = newCat.name.trim();
    if (!name) return;
    if (categories.some((c) => c.name.toLowerCase() === name.toLowerCase())) {
      alert("That category already exists.");
      return;
    }
    const next = [...customCats, { name, kind: newCat.kind }];
    try {
      await saveCustomCategories(next);
      setCustomCats(next);
      if (showNewCat && showNewCat !== true) updateLine(showNewCat, { category: name, kind: newCat.kind });
      setShowNewCat(null);
    } catch (e) {
      console.error(e);
      alert("Could not save the new category.");
    }
  };

  const draftCalc = editing ? summarizeMonth(editing, categories) : null;

  const save = async () => {
    if (editing.moneyOut === "" && editing.moneyIn === "") {
      alert("Enter at least the statement's Money Out total.");
      return;
    }
    if (draftCalc.overAllocated > 0.009) {
      if (!window.confirm(`Your breakdown is ${formatMoney(draftCalc.overAllocated)} more than Money Out. Save anyway?`)) return;
    }
    const clean = {
      accountId: editing.accountId,
      accountLabel: editing.accountLabel,
      accountType: editing.accountType,
      month: editing.month,
      moneyIn: parseAmount(editing.moneyIn),
      moneyOut: parseAmount(editing.moneyOut),
      lines: editing.lines
        .filter((l) => l.category && parseAmount(l.amount))
        .map((l) => ({ id: l.id, category: l.category, kind: lineKind(l, categories), amount: parseAmount(l.amount), note: l.note || "" })),
      notes: editing.notes || "",
      updatedAt: serverTimestamp(),
    };
    setSaving(true);
    try {
      const id = `${editing.accountId}_${editing.month}`;
      await setDoc(doc(db, "bankMonthlySummaries", id), clean);
      setSummaries((prev) => [...prev.filter((s) => s.id !== id), { id, ...clean }]);
      setEditing(null);
    } catch (e) {
      console.error(e);
      alert("Could not save this month.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete the ${monthLabel(editing.month)} summary for ${editing.accountLabel}?`)) return;
    try {
      await deleteDoc(doc(db, "bankMonthlySummaries", editing.id));
      setSummaries((prev) => prev.filter((s) => s.id !== editing.id));
      setEditing(null);
    } catch (e) {
      console.error(e);
      alert("Could not delete.");
    }
  };

  const categorySelect = (line) => (
    <select className="fin-input fin-input--full" value={line.category} onChange={(e) => pickCategory(line.id, e.target.value)}>
      <option value="">Choose category…</option>
      {KIND_ORDER.map((k) => (
        <optgroup key={k} label={KINDS[k].label}>
          {categories.filter((c) => c.kind === k).map((c) => (
            <option key={c.name} value={c.name}>{c.name}</option>
          ))}
        </optgroup>
      ))}
      {line.category && !categories.some((c) => c.name === line.category) && <option value={line.category}>{line.category}</option>}
      <option value="__new__">+ Add new category…</option>
    </select>
  );

  /* ------------------------------ render ------------------------------ */

  return (
    <div className="fin-page fin-page--embedded">
      <header className="fin-header">
        <div>
          <h2 className="fin-title fin-title--sm">Monthly Bank Summary</h2>
          <p className="fin-subtitle">
            Months with an uploaded statement fill in automatically. For any account or month you didn't upload, type the
            totals here and say what the money out was for. Profit Sharing uses both.
          </p>
        </div>
        <div className="fin-header__actions">
          <label className="fin-inline">
            <span>Month</span>
            <input className="fin-input" type="month" value={month} max={thisMonth()} onChange={(e) => e.target.value && setMonth(e.target.value)} />
          </label>
          <button className="fin-btn fin-btn--secondary" onClick={() => { setNewCat({ name: "", kind: "business" }); setShowNewCat(true); }}>
            + Category
          </button>
        </div>
      </header>

      <div className="fin-note fin-note--sm">
        <b>Money in is for your records only.</b> Profit counts customer payments from Deposits and the Job Estimate Spreadsheet,
        so transfers between your accounts never inflate it. <b>Owner draws</b> (personal) don't reduce profit.{" "}
        <b>Owner reimbursable</b> does, because it's a business cost. Money out you don't break down is counted as a
        business expense.
      </div>

      {!loading && missingCount > 0 && (
        <div className="fin-alert fin-alert--sm">
          <div className="fin-alert__icon" aria-hidden>!</div>
          <div className="fin-alert__body">
            <strong>{missingCount} account{missingCount === 1 ? " still needs" : "s still need"} {monthLabel(month)} entered</strong>
            <span>Profit Sharing will warn until every business account has this month.</span>
          </div>
        </div>
      )}

      <section className="fin-stats fin-stats--5">
        <div className="fin-stat"><span className="fin-stat__label">Money in</span><span className="fin-stat__value">{formatMoney(monthTotals.moneyIn)}</span><span className="fin-stat__sub">records only</span></div>
        <div className="fin-stat"><span className="fin-stat__label">Money out</span><span className="fin-stat__value">{formatMoney(monthTotals.moneyOut)}</span></div>
        <div className="fin-stat"><span className="fin-stat__label">Business expenses</span><span className="fin-stat__value">{formatMoney(monthTotals.business)}</span><span className="fin-stat__sub">counts against profit</span></div>
        <div className="fin-stat"><span className="fin-stat__label">Owner draws</span><span className="fin-stat__value">{formatMoney(monthTotals.owner)}</span><span className="fin-stat__sub">personal · not counted</span></div>
        <div className="fin-stat"><span className="fin-stat__label">Transfers</span><span className="fin-stat__value">{formatMoney(monthTotals.transfer)}</span><span className="fin-stat__sub">ignored</span></div>
      </section>

      <section className="fin-card">
        <div className="fin-table-wrap">
          <table className="fin-table">
            <thead>
              <tr>
                <th>Account</th>
                <th>Status</th>
                <th className="fin-num">Money In</th>
                <th className="fin-num">Money Out</th>
                <th className="fin-num">Business</th>
                <th className="fin-num">Owner Draws</th>
                <th className="fin-num">Transfers</th>
                <th className="fin-num">Not broken down</th>
                <th className="fin-right"></th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan="9" className="fin-empty">Loading…</td></tr>}
              {!loading && accounts.length === 0 && (
                <tr><td colSpan="9" className="fin-empty">No bank accounts yet. Add them on the Statement Entries tab under “Add Bank / Account”.</td></tr>
              )}
              {rowsForMonth.map(({ account, summary, fromUpload, typedIgnored, calc }) => (
                <tr key={account.id}>
                  <td className="fin-strong">{accountLabel(account)}</td>
                  <td>
                    {summary && fromUpload ? (
                      <span className="fin-tag fin-tag--bank" title={typedIgnored ? "A typed summary also exists; the upload is used instead" : undefined}>
                        Uploaded{typedIgnored ? " *" : ""}
                      </span>
                    ) : summary ? (
                      <span className="fin-tag fin-tag--sheet">Typed</span>
                    ) : account.accountType === "personal" ? (
                      <span className="fin-tag">Optional</span>
                    ) : (
                      <span className="fin-tag fin-tag--missing">Missing</span>
                    )}
                  </td>
                  <td className="fin-num">{calc ? formatMoney(calc.moneyIn) : "—"}</td>
                  <td className="fin-num">{calc ? formatMoney(calc.moneyOut) : "—"}</td>
                  <td className="fin-num">{calc ? formatMoney(calc.businessOut) : "—"}</td>
                  <td className="fin-num">{calc ? formatMoney(calc.byKind.owner_draw) : "—"}</td>
                  <td className="fin-num">{calc ? formatMoney(calc.byKind.transfer) : "—"}</td>
                  <td className="fin-num">{calc ? (calc.uncategorized ? <span className="fin-warn-inline">{formatMoney(calc.uncategorized)}</span> : "—") : "—"}</td>
                  <td className="fin-right">
                    {fromUpload ? (
                      <button className="fin-btn fin-btn--sm fin-btn--ghost" onClick={() => openUploaded(account, summary)}>View</button>
                    ) : (
                      <button className={`fin-btn fin-btn--sm ${summary ? "fin-btn--ghost" : "fin-btn--primary"}`} onClick={() => openEditor(account)}>
                        {summary ? "Edit" : "Enter month"}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* ------------------------------ editor ------------------------------ */}
      {editing && (
        <BankPopup open finStyles width={1000} onClose={() => setEditing(null)} label="Monthly bank summary">
            <div className="fin-modal__head">
              <div>
                <h2>{editing.accountLabel}</h2>
                <p className="fin-modal__sub">{monthLabel(editing.month)} statement</p>
              </div>
              <button className="fin-icon-btn" onClick={() => setEditing(null)} aria-label="Close">×</button>
            </div>
            <div className="fin-modal__body">
              {editing.readOnly ? (
                <>
                  <div className="fin-note fin-note--sm">
                    This month comes from the {editing.entryCount} line{editing.entryCount === 1 ? "" : "s"} uploaded on Statement Entries.
                    To change what counts as personal, edit those lines there. Nothing typed here is added on top, so it's never counted twice.
                  </div>
                  <table className="fin-table fin-table--compact">
                    <thead><tr><th>Category</th><th>Counts as</th><th className="fin-num">Amount</th></tr></thead>
                    <tbody>
                      {[...editing.lines].sort((a, b) => b.amount - a.amount).map((l) => (
                        <tr key={l.id}>
                          <td>{l.category}</td>
                          <td><span className={`fin-kind fin-kind--${l.kind}`}>{KINDS[l.kind]?.short}</span></td>
                          <td className="fin-num">{formatMoney(l.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              ) : (
              <>
              <div className="fin-form fin-form--3">
                <label className="fin-field">
                  <span>Money In (statement total)</span>
                  <input type="number" step="0.01" value={editing.moneyIn} onChange={(e) => setEditing((p) => ({ ...p, moneyIn: e.target.value }))} />
                </label>
                <label className="fin-field">
                  <span>Money Out (statement total) <em>*</em></span>
                  <input type="number" step="0.01" value={editing.moneyOut} onChange={(e) => setEditing((p) => ({ ...p, moneyOut: e.target.value }))} />
                </label>
                <div className="fin-field">
                  <span>&nbsp;</span>
                  {statementTotals ? (
                    <button
                      className="fin-btn fin-btn--secondary"
                      onClick={() => setEditing((p) => ({ ...p, moneyIn: statementTotals.moneyIn.toFixed(2), moneyOut: statementTotals.moneyOut.toFixed(2) }))}
                      title={`${statementTotals.count} lines already on Statement Entries for this account and month`}
                    >
                      Fill from uploaded statement
                    </button>
                  ) : (
                    <small className="fin-muted">Type the totals from the statement.</small>
                  )}
                </div>
              </div>

              <h3 className="fin-section-title">What was the money out for?</h3>
              <div className="fin-lines">
                <div className="fin-lines__head">
                  <span>Category</span>
                  <span>Amount</span>
                  <span>Note</span>
                  <span></span>
                </div>
                {editing.lines.map((l) => {
                  const k = l.category ? lineKind(l, categories) : "";
                  return (
                    <div key={l.id} className="fin-lines__row">
                      <div>
                        {categorySelect(l)}
                        {k && <span className={`fin-kind fin-kind--${k}`}>{KINDS[k].short}</span>}
                      </div>
                      <input className="fin-input fin-input--full fin-num-input" type="number" step="0.01" placeholder="0.00" value={l.amount} onChange={(e) => updateLine(l.id, { amount: e.target.value })} />
                      <input className="fin-input fin-input--full" placeholder="Optional" value={l.note} onChange={(e) => updateLine(l.id, { note: e.target.value })} />
                      <button className="fin-icon-btn" onClick={() => removeLine(l.id)} aria-label="Remove line">×</button>
                    </div>
                  );
                })}
                <button className="fin-linkbtn" onClick={() => setEditing((p) => ({ ...p, lines: [...p.lines, newLine()] }))}>
                  + Add line
                </button>
              </div>
              </>
              )}

              <div className="fin-breakout">
                {editing.readOnly && <div><span>Money in</span><b>{formatMoney(draftCalc.moneyIn)}</b></div>}
                <div><span>Money out</span><b>{formatMoney(draftCalc.moneyOut)}</b></div>
                <div><span>Business expenses</span><b>{formatMoney(draftCalc.byKind.business)}</b></div>
                <div><span>Owner reimbursable</span><b>{formatMoney(draftCalc.byKind.owner_reimbursable)}</b></div>
                <div><span>Owner draws (personal)</span><b>{formatMoney(draftCalc.byKind.owner_draw)}</b></div>
                <div><span>Transfers</span><b>{formatMoney(draftCalc.byKind.transfer)}</b></div>
                {draftCalc.byKind.repayment > 0 && <div><span>Paying back reimbursable</span><b>{formatMoney(draftCalc.byKind.repayment)}</b></div>}
                {draftCalc.reimbursableIn > 0 && <div><span>Reimbursable money in (owed back)</span><b>{formatMoney(draftCalc.reimbursableIn)}</b></div>}
                <div className={draftCalc.uncategorized ? "is-warn" : ""}>
                  <span>Not broken down (counted as business)</span>
                  <b>{formatMoney(draftCalc.uncategorized)}</b>
                </div>
                {draftCalc.overAllocated > 0 && (
                  <div className="is-bad"><span>Breakdown is more than Money Out by</span><b>{formatMoney(draftCalc.overAllocated)}</b></div>
                )}
                <div className="is-total"><span>Counts against profit</span><b>{formatMoney(draftCalc.businessOut)}</b></div>
              </div>

              {draftCalc.byKind.owner_draw > 0 && (
                <div className="fin-owner-list">
                  <b>Owner draw breakdown:</b>{" "}
                  {draftCalc.byCategory
                    .filter((c) => c.kind === "owner_draw")
                    .map((c) => `${c.category} ${formatMoney(c.amount)}`)
                    .join(" · ")}
                </div>
              )}

              {!editing.readOnly && (
                <label className="fin-field fin-field--full" style={{ marginTop: 14 }}>
                  <span>Notes</span>
                  <input value={editing.notes || ""} onChange={(e) => setEditing((p) => ({ ...p, notes: e.target.value }))} />
                </label>
              )}
            </div>
            {editing.readOnly ? (
              <div className="fin-modal__foot">
                <button className="fin-btn fin-btn--primary" onClick={() => setEditing(null)}>Close</button>
              </div>
            ) : (
            <div className="fin-modal__foot fin-modal__foot--split">
              {summaries.some((s) => s.id === editing.id) ? (
                <button className="fin-btn fin-btn--ghost-danger" onClick={remove}>Delete month</button>
              ) : <span />}
              <div className="fin-actions-right">
                <button className="fin-btn fin-btn--secondary" onClick={() => setEditing(null)}>Cancel</button>
                <button className="fin-btn fin-btn--primary" onClick={save} disabled={saving}>{saving ? "Saving…" : "Save month"}</button>
              </div>
            </div>
            )}
        </BankPopup>
      )}

      {/* --------------------------- new category --------------------------- */}
      {showNewCat && (
        <BankPopup open finStyles layer={2} width={440} onClose={() => setShowNewCat(null)} label="New category">
            <div className="fin-modal__head">
              <h2>New Category</h2>
              <button className="fin-icon-btn" onClick={() => setShowNewCat(null)} aria-label="Close">×</button>
            </div>
            <div className="fin-modal__body">
              <div className="fin-form fin-form--1">
                <label className="fin-field">
                  <span>Name</span>
                  <input autoFocus value={newCat.name} onChange={(e) => setNewCat((p) => ({ ...p, name: e.target.value }))} placeholder="e.g. Personal Gym" />
                </label>
                <label className="fin-field">
                  <span>How should it count?</span>
                  <select value={newCat.kind} onChange={(e) => setNewCat((p) => ({ ...p, kind: e.target.value }))}>
                    {KIND_ORDER.map((k) => (
                      <option key={k} value={k}>{KINDS[k].label}</option>
                    ))}
                  </select>
                </label>
              </div>
              {customCats.length > 0 && (
                <p className="fin-small fin-muted" style={{ marginTop: 12 }}>
                  Your added categories: {customCats.map((c) => c.name).join(", ")}
                </p>
              )}
            </div>
            <div className="fin-modal__foot">
              <button className="fin-btn fin-btn--secondary" onClick={() => setShowNewCat(null)}>Cancel</button>
              <button className="fin-btn fin-btn--primary" onClick={addCategory} disabled={!newCat.name.trim()}>Add category</button>
            </div>
        </BankPopup>
      )}
    </div>
  );
}
